// @ts-check
import { setPaymentTerminalAuthCode } from '@/api/paymentTerminals'
import { service } from '@/core/constants'
import { ROUTE_NAME } from '@/router/meta'
import { store } from '@/store'
import * as _ from 'lodash-es'
import { defineAsyncComponent } from 'vue'

/** @type {import('vue-router').RouteRecordRaw} */
const COMPANY_BASIC_SETTINGS = {
  path: 'company-basic-settings',
  name: 'company_basic_settings',
  component: () =>
    import('@/views/settings/company-details/CompanyAllInformationIndex.vue'),
  meta: {
    requiresAuth: true,
    showInMainNav: false,
    icon: '$briefcase-variant-outline',
    [ROUTE_NAME]: 'company_basic_settings'
  },
  children: [
    {
      path: 'company-settings',
      name: 'company_settings',
      component: () => import('@/components/settings/CompanyBasicSettings.vue'),
      meta: {
        requiresAuth: true,
        icon: '$briefcase-variant-outline',
        [ROUTE_NAME]: 'company_settings'
      }
    },
    {
      path: 'company-other-settings',
      name: 'company_other_settings',
      component: () =>
        import('@/views/settings/company-details/CompanyOtherSettings.vue'),
      meta: {
        requiresAuth: true,
        icon: '$cube-outline',
        [ROUTE_NAME]: 'company_other_settings'
      }
    }
  ]
}

/** @type {import('vue-router').RouteRecordRaw} */
const LTD_COMPANY_BASIC_SETTINGS = {
  path: 'company-details',
  name: 'ltd_company_basic_settings',
  component: () =>
    import('@/views/settings/company-details/CompanyAllInformationIndex.vue'),
  meta: {
    requiresAuth: true,
    showInMainNav: false,
    icon: '$home-account',
    [ROUTE_NAME]: 'ltd_company_basic_settings'
  },
  children: [
    {
      path: 'company-user-settings',
      name: 'company_user_settings',
      component: () =>
        import('@/views/settings/company-details/CompanyUserSettings.vue'),
      meta: {
        requiresAuth: true,
        icon: '$account-outline',
        [ROUTE_NAME]: 'company_user_settings'
      }
    },
    {
      path: 'company-settings',
      name: 'company_settings',
      component: () => import('@/components/settings/CompanyBasicSettings.vue'),
      meta: {
        requiresAuth: true,
        icon: '$briefcase-variant-outline',
        [ROUTE_NAME]: 'company_settings'
      }
    },
    {
      path: 'company-shares-and-board-settings',
      name: 'company_shares_and_board_settings',
      component: () =>
        import('@/views/settings/company-details/CompanyBoardSettings.vue'),
      meta: {
        requiresAuth: true,
        icon: '$chart-pie-outline',
        [ROUTE_NAME]: 'company_shares_and_board_settings'
      }
    },
    {
      path: 'company-other-settings',
      name: 'company_other_settings',
      component: () =>
        import('@/views/settings/company-details/CompanyOtherSettings.vue'),
      meta: {
        requiresAuth: true,
        icon: '$cube-outline',
        [ROUTE_NAME]: 'company_other_settings'
      }
    }
  ]
}

/** @type {import('vue-router').RouteRecordRaw} */
const DOCUMENTS = {
  path: 'documents',
  name: 'documents',
  component: () => import('@/views/settings/documents/DocumentsView.vue'),
  meta: {
    requiresAuth: true,
    showInMainNav: false,
    icon: '$briefcase-variant-outline',
    [ROUTE_NAME]: 'documents'
  }
}

/** @type {import('vue-router').RouteRecordRaw} */
const BANK_ACCOUNT_CONNECTIONS = {
  path: 'bank-accounts',
  name: 'bank_accounts_temp',
  component: () =>
    import('@/views/settings/bank-accounts/BankAccountsIndex.vue'),
  meta: {
    requiresAuth: true,
    showInMainNav: false,
    icon: '$piggy-bank-outline',
    hideSubroutes: true,
    [ROUTE_NAME]: 'bank_accounts'
  },
  redirect: { name: 'bank_accounts' },
  children: [
    {
      path: '',
      name: 'bank_accounts',
      component: () =>
        import('@/components/settings/BankAccounts/BankAccountsConnection.vue'),
      meta: {
        requiresAuth: true,
        icon: '$piggy-bank-outline',
        [ROUTE_NAME]: 'bank_accounts',
        extra: defineAsyncComponent(
          () =>
            import(
              '@/components/business-account/BusinessAccountAdvertisement.vue'
            )
        ),
        extraProps: { class: 'shadow-card border border-secondary-200' }
      }
    },
    {
      path: 'identification',
      name: 'bank_account_connection',
      component: () =>
        import('@/views/settings/bank-accounts/BankAccountIdentification.vue'),
      meta: {
        requiresAuth: true,
        icon: '$piggy-bank-outline',
        [ROUTE_NAME]: 'bank_account_connection'
      }
    },
    {
      path: 'select-accounts',
      name: 'select_bank_accounts',
      component: () =>
        import('@/views/settings/bank-accounts/SelectAccounts.vue'),
      meta: {
        requiresAuth: true,
        icon: '$piggy-bank-outline',
        [ROUTE_NAME]: 'select_bank_accounts'
      }
    },
    {
      path: 'connection-completed',
      name: 'connection_completed',
      component: () =>
        import('@/views/settings/bank-accounts/BankAccountConnected.vue'),
      meta: {
        requiresAuth: true,
        icon: '$piggy-bank-outline',
        [ROUTE_NAME]: 'connection_completed'
      }
    }
  ]
}

/** @type {import('vue-router').RouteRecordRaw} */
const PURCHASE_INVOICE_SETTINGS = {
  path: 'purchase-invoice-settings',
  name: 'purchase_invoice_settings',
  component: () => import('@/components/settings/PurchaseInvoiceSettings.vue'),
  meta: {
    requiresAuth: true,
    showInMainNav: false,
    icon: '$file-outline',
    [ROUTE_NAME]: 'purchase_invoice_settings'
  }
}

/** @type {import('vue-router').RouteRecordRaw} */
const VEHICLE_DETAILS = {
  path: 'vehicles',
  name: 'vehicles',
  component: () =>
    import('@/views/settings/vehicle-details/VehicleDetails.vue'),
  beforeEnter: (to, from, next) => {
    /**
     * Do not allow users to enter vehicle details in KYY without being a courier
     */
    if (!store.getters['app/isTlpService']) {
      next()
    } else if (
      store.getters['app/isTlpService'] &&
      store.getters['companyData/isCourier']
    ) {
      next()
    } else {
      next({ name: 'settings', replace: true })
    }
  },
  meta: {
    requiresAuth: true,
    icon: '$car-side',
    hideSubroutes: true,
    [ROUTE_NAME]: 'vehicle_details'
  },
  redirect: { name: 'vehicle_details' },
  children: [
    {
      path: '',
      name: 'vehicle_details',
      component: () =>
        import('@/views/settings/vehicle-details/VehicleIndex.vue'),
      meta: {
        requiresAuth: true,
        icon: '$car-side',
        [ROUTE_NAME]: 'vehicle_details'
      }
    },
    {
      path: 'create',
      name: 'vehicle_create',
      component: () =>
        import('@/views/settings/vehicle-details/VehicleCreateShow.vue'),
      meta: {
        requiresAuth: true,
        icon: '$car-side',
        [ROUTE_NAME]: 'vehicle_create'
      }
    },
    {
      path: ':id',
      name: 'vehicle_update',
      component: () =>
        import('@/views/settings/vehicle-details/VehicleCreateShow.vue'),
      meta: {
        showInMainNav: false,
        requiresAuth: true,
        icon: '$car-side',
        [ROUTE_NAME]: 'vehicle_update'
      }
    }
  ]
}

/** @type {import('vue-router').RouteRecordRaw} */
const PAYMENT_TERMINALS = {
  path: 'payment-terminals',
  name: 'payment_terminals',
  component: () =>
    import('@/views/settings/payment-terminals/PaymentTerminalContainer.vue'),
  beforeEnter: (to, _, next) => {
    if (to.query.state && to.query.code) {
      setPaymentTerminalAuthCode(to.query.state, to.query.code)
        .then(() => {
          next({ name: 'payment_terminals' })
        })
        .catch(() => {
          next({
            name: 'payment_terminals',
            query: { failed_integration: null }
          })
        })
    } else {
      next()
    }
  },
  meta: {
    requiresAuth: true,
    showInMainNav: false,
    icon: '$cash',
    [ROUTE_NAME]: 'payment_terminals'
  }
}

/** @type {import('vue-router').RouteRecordRaw} */
const SUBSCRIPTION_DETAILS = {
  path: 'subscription-details',
  name: 'subscription_details',
  alias: ['payment-methods/success', 'payment-methods/cancel'],
  component: () =>
    import('@/components/settings/SubscriptionDetails/SubscriptionDetails.vue'),
  meta: {
    requiresAuth: true,
    showInMainNav: false,
    mobileTitle: 'subscription_details_ios',
    icon: '$cash',
    [ROUTE_NAME]: 'subscription_details'
  }
}

/** @type {import('vue-router').RouteRecordRaw} */
const REPORTING_DIMENSIONS = {
  path: 'reporting-dimensions',
  name: 'reporting_dimensions',
  component: () =>
    import(
      '@/components/settings/reporting-dimensions/ReportingDimensionDetails.vue'
    ),
  meta: {
    requiresAuth: true,
    showInMainNav: false,
    icon: '$tag-outline',
    [ROUTE_NAME]: 'reporting_dimensions'
  }
}

/** @type {import('vue-router').RouteRecordRaw} */
const YEL = {
  path: 'yel',
  name: 'verification_yel',
  component: () =>
    import('@/components/settings/verifications/VerificationYEL.vue'),
  meta: {
    requiresAuth: true,
    showInMainNav: false,
    icon: '$shield-plus-outline',
    [ROUTE_NAME]: 'verification_yel'
  }
}

/** @type {import('vue-router').RouteRecordRaw} */
const BANK_ACCOUNT_DETAILS_TLP = {
  path: 'bank-account-details',
  name: 'bank_account_details',
  component: () =>
    import(
      '@/components/settings/BankAccountDetails/BankAccountDetailsTLP.vue'
    ),
  meta: {
    requiresAuth: true,
    showInMainNav: false,
    icon: '$piggy-bank-outline',
    [ROUTE_NAME]: 'bank_account_details'
  }
}

const serviceSettingsMap = {
  [service.UKKOPRO]: [
    COMPANY_BASIC_SETTINGS,
    DOCUMENTS,
    BANK_ACCOUNT_CONNECTIONS,
    PURCHASE_INVOICE_SETTINGS,
    SUBSCRIPTION_DETAILS,
    YEL,
    VEHICLE_DETAILS,
    PAYMENT_TERMINALS
  ],
  [service.OY]: [
    LTD_COMPANY_BASIC_SETTINGS,
    DOCUMENTS,
    BANK_ACCOUNT_CONNECTIONS,
    PURCHASE_INVOICE_SETTINGS,
    SUBSCRIPTION_DETAILS,
    YEL,
    PAYMENT_TERMINALS,
    REPORTING_DIMENSIONS
  ],
  [service.TLP]: [
    COMPANY_BASIC_SETTINGS,
    DOCUMENTS,
    BANK_ACCOUNT_DETAILS_TLP,
    PURCHASE_INVOICE_SETTINGS,
    SUBSCRIPTION_DETAILS,
    YEL,
    VEHICLE_DETAILS
  ]
}

export const setupSettingsRoutes = () => {
  const serviceSettings = _.get(
    serviceSettingsMap,
    process.env.VUE_APP_SERVICE ?? service.UKKOPRO
  )

  return [
    {
      path: '/settings',
      name: 'settings',
      redirect: { name: _.first(serviceSettings).name },
      component: () => import('@/views/SettingsView.vue'),
      meta: {
        requiresAuth: true,
        showInMainNav: true,
        icon: '$cog-outline',
        [ROUTE_NAME]: 'settings'
      },
      children: serviceSettings
    }
  ]
}
